import el from "@cypherpotato/el";
import { api } from "../../api";
import { Button, PrimaryButton } from "../../components/button";
import { Card, CardContent, CardHeader } from "../../components/card";
import { CodeEditor } from "../../components/code-editor";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { FileInput } from "../../components/file-input";
import { Checkbox, FormControl, NumberInput, Select, TextArea, TextInput } from "../../components/form";
import { Icon } from "../../components/icon";
import { JsonControl } from "../../components/json-control";
import { Section } from "../../components/layout";
import { ListPickerControl } from "../../components/list-picker";
import { TabControl } from "../../components/tabcontrol";
import { BlockCode, BlockCodeWrapper, Heading, MutedText, Paragraph } from "../../components/text";
import { TtsVoiceControl, TtsVoiceHint } from "../../components/tts-voice-control";
import { Constants } from "../../constants";
import { getDefaultReranker, loadRerankers } from "../../rerankers";
import { displayAlert } from "../../ui/alert";
import { formatFlexibibleCurrency, formatFloat, formatNumber, formatVeryPreciseCurrency } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { PickImageGenModel, PickRerankerModel } from "../../helpers/model-helpers";

function ModelFinderButton({ value, pickModel, ariaLabel }) {
    let selectedModel = value;

    return Button(selectedModel, {
        ariaLabel,
        async onClick(event) {
            const button = event.currentTarget;
            selectedModel = await pickModel(selectedModel);
            button.textContent = selectedModel;
        },
        $properties: {
            value: {
                get() {
                    return selectedModel;
                },
                set(value) {
                    selectedModel = value;
                    this.textContent = value;
                }
            }
        }
    });
}

function getTranscriptionFormat(file) {
    if (!file) {
        return undefined;
    }

    const extension = file.name.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
    const mimeFormats = {
        "audio/wav": "wav",
        "audio/x-wav": "wav",
        "audio/mpeg": "mp3",
        "audio/mp4": "m4a",
        "audio/x-m4a": "m4a",
        "audio/flac": "flac",
        "audio/ogg": "ogg",
        "audio/webm": "webm",
        "audio/aac": "aac"
    };
    return extension === "mp4" ? "m4a" : extension || mimeFormats[file.type];
}

function getContentParts(files) {
    return Promise.all([...(files || [])].map(file =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => {
                const dataUrl = String(reader.result);
                const mediaType = file.type.split("/")[0];

                if (mediaType === "image") {
                    resolve({
                        type: "image_url",
                        image_url: { url: dataUrl }
                    });
                    return;
                }

                if (mediaType === "audio") {
                    resolve({
                        type: "input_audio",
                        input_audio: {
                            data: dataUrl.slice(dataUrl.indexOf(",") + 1),
                            format: file.name.match(/\.([^.]+)$/)?.[1]?.toLowerCase() || file.type.split("/")[1] || "bin"
                        }
                    });
                    return;
                }

                if (mediaType === "video") {
                    resolve({
                        type: "video_url",
                        video_url: { url: dataUrl }
                    });
                    return;
                }

                resolve({
                    type: "file",
                    file: {
                        filename: file.name,
                        file_data: dataUrl
                    }
                });
            };
            reader.readAsDataURL(file);
        })
    ));
}

function TranscriptionModelDetails(model) {
    return el("div.transcription-model-details",
        Paragraph(model?.description || "No additional information is available for this model."),
        model && MutedText(`${Number(model.price) < 0.0001 ? formatVeryPreciseCurrency(model.price) : formatFlexibibleCurrency(model.price)} per ${model.priceUnit}`)
    );
}

function GenerationWorkspace(options) {
    const result = el.state.create(null);

    return el("div.generation-workspace",
        Section(
            el("div.generation-workspace-header",
                Heading(options.title, Paragraph(options.description)),
                CommandBar(
                    PrimaryButton(Icon("play-mini-fill"), options.actionText, {
                        async onClick(event) {
                            const actionButton = event.currentTarget;
                            actionButton.disabled = true;
                            actionButton.setAttribute("aria-busy", "true");

                            try {
                                await runOperation(options.operationText, async () => {
                                    const payload = await options.getPayload();
                                    const response = await api.fetch(options.endpoint, {
                                        method: "POST",
                                        headers: {
                                            "Content-Type": "application/json"
                                        },
                                        body: JSON.stringify(payload)
                                    });
                                    const contentType = response.headers.get("content-type") || "";

                                    if (contentType.includes("json")) {
                                        const responseJson = await api.parseBody(response);
                                        if (!response.ok || responseJson?.error) {
                                            return;
                                        }

                                        if (result.value?.blobUrl) {
                                            URL.revokeObjectURL(result.value.blobUrl);
                                        }
                                        result.value = {
                                            json: responseJson,
                                            payload
                                        };
                                        return;
                                    }

                                    if (!response.ok) {
                                        return;
                                    }

                                    const blob = await response.blob();
                                    if (result.value?.blobUrl) {
                                        URL.revokeObjectURL(result.value.blobUrl);
                                    }
                                    result.value = {
                                        blobUrl: URL.createObjectURL(blob),
                                        mimeType: contentType || blob.type,
                                        payload
                                    };
                                });
                            } finally {
                                actionButton.disabled = false;
                                actionButton.removeAttribute("aria-busy");
                            }
                        }
                    }),
                    Button(Icon("braces-line"), "View code", {
                        async onClick() {
                            const codePayload = options.getCodePayload
                                ? await options.getCodePayload()
                                : await options.getPayload();
                            const endpoint = new URL(options.endpoint, window.env.apiEndpoint).toString();
                            const requestBody = JSON.stringify(codePayload, null, 4).replaceAll("'", "'\\''");
                            displayAlert({
                                title: `${options.title} code`,
                                dialogSize: "large",
                                message: BlockCodeWrapper("Shell request", `curl '${endpoint}' \\
    -H "Authorization: Bearer $AIVAX_API_KEY" \\
    -H 'Content-Type: application/json' \\
    -d '${requestBody}'`, "bash")
                            });
                        }
                    })
                )
            )
        ),
        Section(
            el("div.generation-workspace-grid",
                Card(
                    { class: "bg-card generation-panel generation-parameters-panel" },
                    CardHeader("Parameters", options.endpoint),
                    CardContent(
                        el("div.generation-form", options.parameters)
                    )
                ),
                Card(
                    { class: "bg-card generation-panel generation-result-panel" },
                    CardHeader("Result", "Switch between the rendered preview and the API response."),
                    CardContent(
                        el.state.listening([result], () => {
                            if (!result.value) {
                                return el("div.generation-result-empty",
                                    Icon(options.emptyIcon),
                                    Paragraph(options.emptyText)
                                );
                            }

                            const data = result.value.json?.data;
                            let preview;

                            switch (options.kind) {
                                case "speech": {
                                    const source = result.value.blobUrl || data?.data && `data:${data.mimeType};base64,${data.data}`;
                                    preview = source
                                        ? el("div.speech-generation-preview",
                                            Icon("volume-up-line"),
                                            el("audio[controls]", {
                                                src: source,
                                                ariaLabel: "Generated speech"
                                            })
                                        )
                                        : MutedText("The response did not contain playable audio.");
                                    break;
                                }
                                case "transcription": {
                                    const text = data?.text;
                                    preview = typeof text === "string" && text.trim()
                                        ? el("div.transcription-result",
                                            el("div.transcription-result-heading",
                                                Icon("file-text-line"),
                                                el("strong", "Transcription")
                                            ),
                                            el("p", text),
                                            data.usage && el("dl.generation-usage",
                                                el("div", el("dt", "Processing time"), el("dd", `${formatFloat(data.usage.process_time || 0)} s`)),
                                                el("div", el("dt", "Cost"), el("dd", formatVeryPreciseCurrency(data.usage.cost || 0)))
                                            )
                                        )
                                        : MutedText("The response did not contain a transcription.");
                                    break;
                                }
                                case "segmenter": {
                                    const documents = Array.isArray(data?.result)
                                        ? [...data.result].sort((left, right) => left.index - right.index)
                                        : [];
                                    preview = documents.length
                                        ? el("div.segmentation-results",
                                            ...documents.map(document =>
                                                el("article.segmentation-result",
                                                    el("div.segmentation-result-heading",
                                                        el("strong", `Document ${document.index + 1}`),
                                                        el("span", `${formatNumber(document.count)} segment${document.count === 1 ? "" : "s"}`)
                                                    ),
                                                    el("ol",
                                                        ...(document.segments || []).map(segment => el("li", segment))
                                                    )
                                                )
                                            ),
                                            data.usage && el("dl.generation-usage",
                                                el("div", el("dt", "Total tokens"), el("dd", formatNumber(data.usage.total_tokens || 0))),
                                                el("div", el("dt", "Cost"), el("dd", formatVeryPreciseCurrency(data.usage.cost || 0)))
                                            )
                                        )
                                        : MutedText("The response did not contain text segments.");
                                    break;
                                }
                                case "classifier": {
                                    const documents = Array.isArray(data?.results) ? data.results : [];
                                    preview = documents.length
                                        ? el("div.classification-results",
                                            ...documents.map(document =>
                                                el("article.classification-result",
                                                    el("div.classification-result-heading",
                                                        el("strong", `Document ${document.index + 1}`),
                                                        el("span", `${formatNumber(document.scores?.length || 0)} labels`)
                                                    ),
                                                    el("p", document.document),
                                                    el("ol",
                                                        ...(document.scores || []).map(score =>
                                                            el("li",
                                                                el("span", score.label),
                                                                el("code", Number(score.score).toFixed(4))
                                                            )
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                        : MutedText("The response did not contain classifications.");
                                    break;
                                }
                                case "media": {
                                    preview = Array.isArray(data) && data.length
                                        ? el("div.media-description-results",
                                            ...data.map((item, index) =>
                                                el("article.media-description-result",
                                                    BlockCode(JSON.stringify(item, null, 2), "json")
                                                )
                                            )
                                        )
                                        : MutedText("The response did not contain media descriptions.");
                                    break;
                                }
                                case "extraction": {
                                    preview = Array.isArray(data) && data.length
                                        ? el("div.media-description-results",
                                            ...data.map((item, index) =>
                                                el("article.media-description-result",
                                                    el("span", `Document ${index + 1}`),
                                                    BlockCode(JSON.stringify(item, null, 2), "json")
                                                )
                                            )
                                        )
                                        : MutedText("The response did not contain extracted data.");
                                    break;
                                }
                                case "documents": {
                                    preview = Array.isArray(data) && data.length
                                        ? el("div.classification-results",
                                            ...data.map((text, index) =>
                                                el("article.classification-result",
                                                    el("div.classification-result-heading",
                                                        el("strong", `Document ${index + 1}`)
                                                    ),
                                                    el("p", text)
                                                )
                                            )
                                        )
                                        : MutedText("The response did not contain results.");
                                    break;
                                }
                                case "images": {
                                    const imageGroups = Array.isArray(data) ? data : [];
                                    const images = imageGroups.flatMap(group =>
                                        (group.images || []).map(imageUrl => ({
                                            imageUrl,
                                            input: group.input
                                        }))
                                    );
                                    preview = images.length
                                        ? el("div.image-generation-results",
                                            ...images.map(image =>
                                                el("figure",
                                                    el("img", {
                                                        src: image.imageUrl,
                                                        alt: image.input || "Generated image",
                                                        loading: "lazy",
                                                        style: {
                                                            cursor: "pointer"
                                                        },
                                                        onClick(event) {
                                                            displayAlert({
                                                                title: "Generated content",
                                                                dialogSize: "lightbox",
                                                                message: [
                                                                    event.target.cloneNode(true)
                                                                ]
                                                            });
                                                        }
                                                    }),
                                                    image.input && el("figcaption", image.input)
                                                )
                                            )
                                        )
                                        : MutedText("The response did not contain generated images.");
                                    break;
                                }
                                case "reranker": {
                                    const results = result.value.json?.results || [];
                                    preview = results.length
                                        ? el("ol.reranking-results",
                                            ...results.map(item => {
                                                const score = Number(item.relevance_score);
                                                return el("li",
                                                    el("div.reranking-result-heading",
                                                        el("span", Number.isFinite(score) ? score.toFixed(4) : item.relevance_score)
                                                    ),
                                                    el("p", item.document?.text || "")
                                                );
                                            })
                                        )
                                        : MutedText("The response did not contain ranked documents.");
                                    break;
                                }
                            }

                            return el("div.generation-result-viewer",
                                TabControl([
                                    {
                                        title: "Preview",
                                        icon: Icon("eye-line"),
                                        contents: el("div.generation-preview", preview)
                                    },
                                    result.value.json && {
                                        title: "JSON",
                                        icon: Icon("braces-line"),
                                        contents: JsonControl({
                                            value: result.value.json,
                                            readonly: true,
                                            ariaLabel: "JSON response"
                                        })
                                    }
                                ].filter(Boolean))
                            );
                        })
                    )
                )
            )
        )
    );
}

export default async function () {
    const [imageModelsResponse, transcriptionModelsResponse, rerankers] = await Promise.all([
        api.getJson("/api/v1/information/image-models.json"),
        api.getJson("/api/v1/information/audio-transcription-models.json"),
        loadRerankers()
    ]);
    const imageModelItems = (imageModelsResponse?.data || []).map(model => ({
        value: model.name,
        text: model.name
    }));
    if (imageModelItems.length === 0) {
        imageModelItems.push({
            value: "@alibaba/wan-image-2.7-pro",
            text: "@alibaba/wan-image-2.7-pro"
        });
    }

    const speechModel = Select({
        value: "Gpt4oTts",
        ariaLabel: "Speech model",
        items: Object.entries(Constants.TtsModels).map(([value, model]) => ({ value, text: model.name })),
        onChange(event) {
            const modelName = event.currentTarget.value;
            const nextVoice = TtsVoiceControl(modelName, Constants.TtsModels[modelName].defaultVoice, {
                ariaLabel: "Speech voice"
            });
            const nextVoiceHint = TtsVoiceHint(modelName);

            speechVoice.replaceWith(nextVoice);
            speechVoiceHint.replaceWith(nextVoiceHint);
            speechVoice = nextVoice;
            speechVoiceHint = nextVoiceHint;
        }
    });
    const speechInput = TextArea(null, "Hello! This audio was generated by AIVAX.", {
        rows: 6,
        ariaLabel: "Speech input"
    });
    let speechVoice = TtsVoiceControl("Gpt4oTts", Constants.TtsModels.Gpt4oTts.defaultVoice, {
        ariaLabel: "Speech voice"
    });
    let speechVoiceHint = TtsVoiceHint("Gpt4oTts");
    const speechFormat = Select({
        value: "wav",
        ariaLabel: "Audio format",
        items: [
            { value: "wav", text: "WAV" },
            { value: "mp4", text: "MP4" }
        ]
    });
    const speechRaw = Select({
        value: "false",
        ariaLabel: "Speech response format",
        items: [
            { value: "false", text: "JSON with base64 audio" },
            { value: "true", text: "Raw audio" }
        ]
    });

    const transcriptionModels = transcriptionModelsResponse?.data || [];
    const defaultTranscriptionModel = transcriptionModels.find(model => model.isDefault) || transcriptionModels[0];
    let transcriptionModelDetails;
    const transcriptionModel = Select({
        value: defaultTranscriptionModel?.name || "@x-ai/grok-stt-1.0",
        ariaLabel: "Speech-to-Text model",
        items: transcriptionModels.length
            ? transcriptionModels.map(model => ({ value: model.name, text: model.displayName || model.name }))
            : [{ value: "@x-ai/grok-stt-1.0", text: "xAI: Grok STT 1.0" }],
        onChange(event) {
            const selectedModel = transcriptionModels.find(model => model.name === event.currentTarget.value);
            const nextDetails = TranscriptionModelDetails(selectedModel);
            transcriptionModelDetails.replaceWith(nextDetails);
            transcriptionModelDetails = nextDetails;
        }
    });
    transcriptionModelDetails = TranscriptionModelDetails(defaultTranscriptionModel);
    const transcriptionInput = FileInput({
        accept: "audio/wav,audio/mpeg,audio/mp4,audio/x-m4a,audio/flac,audio/ogg,audio/webm,audio/aac",
        ariaLabel: "Choose an audio file"
    });
    const transcriptionLanguage = TextInput(null, "Optional, for example: pt", {
        ariaLabel: "Transcription language"
    });

    const segmenterDocument = CodeEditor({
        value: "AIVAX indexes documents with embeddings.\nSemantic search retrieves the most relevant passages.\nReranking can refine their order.",
        language: "plaintext",
        height: "320px",
        editorProps: {
            ariaLabel: "Document to segment"
        }
    });
    const segmenterSanitize = Checkbox(null, "Remove content that is not useful for retrieval", {
        ariaLabel: "Sanitize documents before segmentation"
    });

    const classifierDocuments = ListPickerControl({
        value: [
            "Calculate the compound interest on a principal of $10,000 invested for 5 years at an annual rate of 5%, compounded quarterly.",
            "Write a poem about the beauty of nature and its healing power on the human soul."
        ],
        placeholder: "Add document",
        ariaLabel: "Add a document to classify",
        itemName: "document",
        includePendingValue: true,
        allowEmptyItems: true,
        allowDuplicateItems: true
    });
    const classifierLabels = ListPickerControl({
        value: ["Creative writing", "Complex problem", "Simple task"],
        placeholder: "Add label",
        ariaLabel: "Add a classification label",
        itemName: "label",
        includePendingValue: true,
        allowEmptyItems: true,
        allowDuplicateItems: true
    });

    const mediaInput = FileInput({
        multiple: true,
        ariaLabel: "Choose media files"
    });

    const extractionSchema = JsonControl({
        value: {
            type: "object",
            properties: {
                invoice_number: { type: "string", description: "Invoice number" },
                vendor: { type: "string", description: "Company that issued the invoice" },
                total: { type: "number", description: "Total amount due" }
            }
        },
        ariaLabel: "Extraction response schema"
    });
    const extractionInstructions = TextArea(null, "", {
        rows: 3,
        placeholder: "Optional",
        ariaLabel: "Extraction instructions"
    });
    const extractionDocuments = ListPickerControl({
        value: ["Invoice #1042 from Acme Corp. Total due: $20.00."],
        placeholder: "Add document",
        ariaLabel: "Add a text document to extract from",
        itemName: "document",
        includePendingValue: true
    });
    const extractionFiles = FileInput({
        multiple: true,
        accept: "image/jpeg,image/png,image/tiff,application/pdf",
        ariaLabel: "Choose documents to extract from"
    });

    const summarizationInstructions = TextArea(null, "", {
        rows: 3,
        placeholder: "Optional",
        ariaLabel: "Summarization instructions"
    });
    const summarizationDocuments = ListPickerControl({
        value: [],
        placeholder: "Add document",
        ariaLabel: "Add a text document to summarize",
        itemName: "document",
        includePendingValue: true
    });
    const summarizationFiles = FileInput({
        multiple: true,
        accept: "image/jpeg,image/png,image/tiff,application/pdf",
        ariaLabel: "Choose documents to summarize"
    });

    const translationToLanguage = TextInput(null, "For example: English, pt-BR or Japanese (formal)", {
        value: "Brazilian Portuguese",
        ariaLabel: "Target language"
    });
    const translationFromLanguage = TextInput(null, "Detected from the content when empty", {
        ariaLabel: "Source language"
    });
    const translationDocuments = ListPickerControl({
        value: ["Your order has shipped and should arrive within three business days."],
        placeholder: "Add document",
        ariaLabel: "Add a text document to translate",
        itemName: "document",
        includePendingValue: true
    });
    const translationFiles = FileInput({
        multiple: true,
        accept: "image/jpeg,image/png,image/webp,image/gif,audio/*",
        ariaLabel: "Choose images or audio to translate"
    });

    const imageInput = TextArea(null, "A kitten playing with a red ball on a sunlit floor", {
        rows: 6,
        ariaLabel: "Image generation input"
    });
    const imageCount = NumberInput({
        value: 1,
        min: 1,
        max: 4,
        step: 1,
        ariaLabel: "Image count"
    });
    const imageModel = ModelFinderButton({
        value: imageModelItems.some(model => model.value === "@alibaba/wan-image-2.7-pro")
            ? "@alibaba/wan-image-2.7-pro"
            : imageModelItems[0].value,
        ariaLabel: "Image model",
        pickModel: selectedModel => PickImageGenModel({ selectedModel })
    });
    const referenceImages = ListPickerControl({
        value: []
    });

    const autonomousRerankers = rerankers.filter(reranker => reranker.autonomousUse);
    const defaultReranker = getDefaultReranker(autonomousRerankers);
    const rerankerModel = ModelFinderButton({
        value: defaultReranker.name,
        ariaLabel: "Reranker model",
        pickModel: selectedModel => PickRerankerModel({
            selectedModel,
            filter: reranker => reranker.autonomousUse
        })
    });
    const rerankerQuery = TextArea(null, "A kitten playing with a ball", {
        rows: 4,
        ariaLabel: "Reranker query"
    });
    const rerankerDocuments = ListPickerControl({
        value: [
            "A cat chases a red ball across the floor.",
            "A guide to growing tomatoes.",
            "Kittens need frequent play and exercise."
        ]
    });
    const rerankerTopN = NumberInput({
        value: 3,
        min: 1,
        max: 200,
        step: 1,
        ariaLabel: "Top ranked documents"
    });

    return Dashboard(
        DashboardTitle("Generations", "Run speech, transcription, text processing, document, translation, media, image and reranking APIs from one workspace."),
        el("div.generations-page",
            TabControl([
                {
                    title: "Speech (TTS)",
                    icon: Icon("volume-up-line"),
                    contents: GenerationWorkspace({
                        kind: "speech",
                        title: "Speech Generation",
                        description: "Turn text into WAV or MP4 audio with the selected voice provider.",
                        endpoint: "/api/v1/generations/speech",
                        actionText: "Generate speech",
                        operationText: "Generating speech...",
                        emptyIcon: "volume-up-line",
                        emptyText: "Generated audio will be playable here.",
                        parameters: el.fragment(
                            FormControl("Model",
                                speechModel,
                                MutedText("Select the text-to-speech provider.")
                            ),
                            FormControl("Input",
                                speechInput,
                                MutedText("Text that will be synthesized into speech.")
                            ),
                            FormControl("Voice",
                                speechVoice,
                                speechVoiceHint
                            ),
                            FormControl("Audio format", speechFormat),
                            FormControl("Response", speechRaw)
                        ),
                        getPayload() {
                            return {
                                model: speechModel.value,
                                input: speechInput.value,
                                voice: speechVoice.value || null,
                                format: speechFormat.value,
                                raw: speechRaw.value === "true"
                            };
                        }
                    })
                },
                {
                    title: "Speech-to-Text",
                    icon: Icon("file-text-line"),
                    contents: GenerationWorkspace({
                        kind: "transcription",
                        title: "Speech-to-Text",
                        description: "Transcribe a supported audio file into text with OpenRouter.",
                        endpoint: "/api/v1/generations/audio/transcriptions",
                        actionText: "Transcribe audio",
                        operationText: "Transcribing audio...",
                        emptyIcon: "file-text-line",
                        emptyText: "The transcription will appear here.",
                        parameters: el.fragment(
                            FormControl("Model",
                                transcriptionModel,
                                transcriptionModelDetails
                            ),
                            FormControl("Audio",
                                transcriptionInput,
                                MutedText("Supports WAV, MP3, M4A, FLAC, OGG, WebM and AAC files.")
                            ),
                            FormControl("Language",
                                transcriptionLanguage,
                                MutedText("Optional language hint, such as pt or en. Leave blank for automatic detection.")
                            )
                        ),
                        async getPayload() {
                            const file = transcriptionInput.value;
                            let audioData = "";
                            if (file) {
                                const dataUrl = await new Promise((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onerror = () => reject(reader.error);
                                    reader.onload = () => resolve(String(reader.result));
                                    reader.readAsDataURL(file);
                                });
                                audioData = dataUrl.slice(dataUrl.indexOf(",") + 1);
                            }

                            return {
                                model: transcriptionModel.value,
                                input_audio: {
                                    data: audioData,
                                    format: getTranscriptionFormat(file)
                                },
                                language: transcriptionLanguage.value.trim() || undefined
                            };
                        },
                        getCodePayload() {
                            return {
                                model: transcriptionModel.value,
                                input_audio: {
                                    data: "<base64 audio omitted>",
                                    format: getTranscriptionFormat(transcriptionInput.value) || "wav"
                                },
                                language: transcriptionLanguage.value.trim() || undefined
                            };
                        }
                    })
                },
                {
                    title: "Segmenter",
                    icon: Icon("scissors-cut-line"),
                    contents: GenerationWorkspace({
                        kind: "segmenter",
                        title: "Text Segmenter",
                        description: "Split documents into semantically cohesive chunks for embedding and RAG ingestion.",
                        endpoint: "/api/v1/generations/segment",
                        actionText: "Segment text",
                        operationText: "Segmenting documents...",
                        emptyIcon: "scissors-cut-line",
                        emptyText: "Semantic segments will appear here grouped by source document.",
                        parameters: el.fragment(
                            FormControl("Document",
                                segmenterDocument,
                                MutedText("Paste a document with one meaningful sentence or passage per line.")
                            ),
                            FormControl("Sanitize",
                                segmenterSanitize,
                                MutedText("When enabled, content judged irrelevant to RAG retrieval may be omitted.")
                            )
                        ),
                        getPayload() {
                            return {
                                documents: [segmenterDocument.value],
                                sanitize: segmenterSanitize.value
                            };
                        }
                    })
                },
                {
                    title: "Text Classifier",
                    icon: Icon("price-tag-3-line"),
                    contents: GenerationWorkspace({
                        kind: "classifier",
                        title: "Text Classifier",
                        description: "Rank a fixed set of labels for each document using semantic similarity.",
                        endpoint: "/api/v1/generations/classify",
                        actionText: "Classify text",
                        operationText: "Classifying documents...",
                        emptyIcon: "price-tag-3-line",
                        emptyText: "Ranked labels will appear here for each document.",
                        parameters: el.fragment(
                            FormControl("Documents",
                                classifierDocuments,
                                MutedText("Add the texts to classify. Press Enter when you want to add another item.")
                            ),
                            FormControl("Labels",
                                classifierLabels,
                                MutedText("Add every candidate category. Press Enter when you want to add another label.")
                            )
                        ),
                        getPayload() {
                            return {
                                documents: classifierDocuments.value,
                                labels: classifierLabels.value
                            };
                        }
                    })
                },
                {
                    title: "Media Descriptions",
                    icon: Icon("file-search-line"),
                    contents: GenerationWorkspace({
                        kind: "media",
                        title: "Media Descriptions",
                        description: "Resolve supported audio, images, video and files into textual descriptions.",
                        endpoint: "/api/v1/generations/descriptions",
                        actionText: "Describe media",
                        operationText: "Describing media...",
                        emptyIcon: "file-search-line",
                        emptyText: "Descriptions will appear here in the same order as the input items.",
                        parameters: el.fragment(
                            FormControl("Input",
                                mediaInput,
                                MutedText("Select one or more supported images, audio, video, PDFs or other files.")
                            )
                        ),
                        async getPayload() {
                            return {
                                input: await getContentParts(mediaInput.value)
                            };
                        }
                    })
                },
                {
                    title: "Extraction",
                    icon: Icon("file-list-3-line"),
                    contents: GenerationWorkspace({
                        kind: "extraction",
                        title: "Data Extraction",
                        description: "Extract structured JSON from text, images and PDFs following a JSON schema.",
                        endpoint: "/api/v1/generations/extract",
                        actionText: "Extract data",
                        operationText: "Extracting data...",
                        emptyIcon: "file-list-3-line",
                        emptyText: "One JSON object per document will appear here, in input order.",
                        parameters: el.fragment(
                            FormControl("Response schema",
                                extractionSchema,
                                MutedText("Object schema whose properties describe the fields to find. Property descriptions guide the extraction.")
                            ),
                            FormControl("Instructions", extractionInstructions),
                            FormControl("Text documents",
                                extractionDocuments,
                                MutedText("Add texts to process. Press Enter when you want to add another item.")
                            ),
                            FormControl("Files",
                                extractionFiles,
                                MutedText("JPEG, PNG, TIFF or PDF files, processed after the text documents.")
                            )
                        ),
                        async getPayload() {
                            return {
                                instructions: extractionInstructions.value.trim() || undefined,
                                responseSchema: extractionSchema.value,
                                documents: [
                                    ...extractionDocuments.value.map(text => ({ type: "text", text })),
                                    ...await getContentParts(extractionFiles.value)
                                ]
                            };
                        }
                    })
                },
                {
                    title: "Summarization",
                    icon: Icon("article-line"),
                    contents: GenerationWorkspace({
                        kind: "documents",
                        title: "Summarization",
                        description: "Summarize text, images and PDFs, optionally guided by instructions.",
                        endpoint: "/api/v1/generations/summarize",
                        actionText: "Summarize",
                        operationText: "Summarizing documents...",
                        emptyIcon: "article-line",
                        emptyText: "One summary per document will appear here, in input order.",
                        parameters: el.fragment(
                            FormControl("Instructions", summarizationInstructions),
                            FormControl("Text documents",
                                summarizationDocuments,
                                MutedText("Add texts to process. Press Enter when you want to add another item.")
                            ),
                            FormControl("Files",
                                summarizationFiles,
                                MutedText("JPEG, PNG, TIFF or PDF files, processed after the text documents.")
                            )
                        ),
                        async getPayload() {
                            return {
                                instructions: summarizationInstructions.value.trim() || undefined,
                                documents: [
                                    ...summarizationDocuments.value.map(text => ({ type: "text", text })),
                                    ...await getContentParts(summarizationFiles.value)
                                ]
                            };
                        }
                    })
                },
                {
                    title: "Translation",
                    icon: Icon("translate-2"),
                    contents: GenerationWorkspace({
                        kind: "documents",
                        title: "Translation",
                        description: "Translate text, the visible text in images and the speech in audio into any language.",
                        endpoint: "/api/v1/generations/translate",
                        actionText: "Translate",
                        operationText: "Translating documents...",
                        emptyIcon: "translate-2",
                        emptyText: "One translation per document will appear here, in input order.",
                        parameters: el.fragment(
                            FormControl("To language", translationToLanguage),
                            FormControl("From language", translationFromLanguage),
                            FormControl("Text documents",
                                translationDocuments,
                                MutedText("Add texts to translate. Press Enter when you want to add another item.")
                            ),
                            FormControl("Files",
                                translationFiles,
                                MutedText("JPEG, PNG, WebP or GIF images and audio files, processed after the text documents.")
                            )
                        ),
                        async getPayload() {
                            return {
                                fromLanguage: translationFromLanguage.value.trim() || undefined,
                                toLanguage: translationToLanguage.value,
                                documents: [
                                    ...translationDocuments.value.map(text => ({ type: "text", text })),
                                    ...await getContentParts(translationFiles.value)
                                ]
                            };
                        }
                    })
                },
                {
                    title: "Image Generation",
                    icon: Icon("image-line"),
                    contents: GenerationWorkspace({
                        kind: "images",
                        title: "Image Generation",
                        description: "Generate one to four images from a prompt, with optional reference images.",
                        endpoint: "/api/v1/generations/images",
                        actionText: "Generate images",
                        operationText: "Generating images...",
                        emptyIcon: "image-line",
                        emptyText: "Generated images will appear in a preview grid.",
                        parameters: el.fragment(
                            FormControl("Input",
                                imageInput,
                                MutedText("Describe the image you want to generate.")
                            ),
                            FormControl("Count",
                                imageCount,
                                MutedText("Number of images generated for the prompt, from one to four.")
                            ),
                            FormControl("Model", imageModel),
                            FormControl("Reference images",
                                referenceImages,
                                MutedText("Add up to four absolute image URLs, pressing Enter after each one.")
                            )
                        ),
                        getPayload() {
                            return {
                                input: imageInput.value,
                                count: imageCount.value,
                                model: imageModel.value,
                                referenceImages: referenceImages.value
                            };
                        }
                    })
                },
                {
                    title: "Rerankers",
                    icon: Icon("sort-desc"),
                    contents: GenerationWorkspace({
                        kind: "reranker",
                        title: "Document Reranking",
                        description: "Rank supplied documents with any autonomous reranker in the current API catalog.",
                        endpoint: "/api/v1/generations/rerank",
                        actionText: "Rank documents",
                        operationText: "Ranking documents...",
                        emptyIcon: "sort-desc",
                        emptyText: "Ranked documents and relevance scores will appear here.",
                        parameters: el.fragment(
                            FormControl("Model",
                                rerankerModel,
                                MutedText("Only rerankers that support autonomous API use are available here.")
                            ),
                            FormControl("Query",
                                rerankerQuery,
                                MutedText("Text used to evaluate document relevance.")
                            ),
                            FormControl("Documents",
                                rerankerDocuments,
                                MutedText("Add documents within the selected model's published limit, pressing Enter after each one.")
                            ),
                            FormControl("Top N",
                                rerankerTopN,
                                MutedText("Number of ranked documents returned by the API, up to 200 in this playground.")
                            )
                        ),
                        getPayload() {
                            return {
                                model: rerankerModel.value,
                                query: rerankerQuery.value,
                                documents: rerankerDocuments.value,
                                top_n: rerankerTopN.value
                            };
                        }
                    })
                }
            ], {
                type: "fixed"
            })
        )
    );
}
